import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Clock, DollarSign, PackageOpen, Plus, Undo2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ReturnDialog } from '@/components/returns/ReturnDialog';
import { ReceiveReturnDialog } from '@/components/returns/ReceiveReturnDialog';
import { ReturnDetailSheet } from '@/components/returns/ReturnDetailSheet';
import {
  CHANNEL_RETURN_PROFILES,
  RESOLUTION_LABELS,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_VARIANTS,
  RETURN_CHANNEL_ORDER,
  RETURN_CLOSED_STATUSES,
  RETURN_REASON_LABELS,
  RETURN_RECEIVABLE_STATUSES,
  RETURN_STATUSES,
  RETURN_STATUS_LABELS,
  RETURN_STATUS_VARIANTS,
  returnDisplayCustomer,
  returnLineLabel,
  returnLinesValueCents,
  useReturns,
  type ReturnReason,
  type ReturnRow,
  type ReturnStatus,
} from '@/features/returns/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDate } from '@/lib/formatters';
import type { SalesChannelKind } from '@/types/database';

type ChannelFilter = 'all' | SalesChannelKind;
type StatusFilter = 'open' | 'all' | ReturnStatus;

const DAY_MS = 24 * 60 * 60 * 1000;

function awaitingReceipt(r: ReturnRow) {
  return r.receipt_mode === 'ship_to_us' && RETURN_RECEIVABLE_STATUSES.includes(r.status);
}

function awaitingResolution(r: ReturnRow) {
  return r.resolution_status === 'pending' && !RETURN_CLOSED_STATUSES.includes(r.status) && r.status !== 'requested';
}

/**
 * Returns / RMA — one queue for Shopify, Amazon, and wholesale returns.
 * Each RMA tracks the physical return (authorize → receive) separately
 * from the refund / credit, because channels settle in different orders.
 */
export function ReturnsPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'sales');
  const { data, isLoading } = useReturns();
  const rows = useMemo(() => data ?? [], [data]);

  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReturnRow | null>(null);
  const [receiving, setReceiving] = useState<ReturnRow | null>(null);

  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  const channelCounts = useMemo(() => {
    const counts: Record<ChannelFilter, number> = { all: 0, shopify: 0, amazon: 0, b2b_portal: 0, manual: 0 };
    for (const r of rows) {
      if (RETURN_CLOSED_STATUSES.includes(r.status)) continue;
      counts.all++;
      counts[r.channel_kind]++;
    }
    return counts;
  }, [rows]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (channel !== 'all' && r.channel_kind !== channel) return false;
        if (status === 'open') return !RETURN_CLOSED_STATUSES.includes(r.status);
        if (status !== 'all') return r.status === status;
        return true;
      }),
    [rows, channel, status],
  );

  const metrics = useMemo(() => {
    const scoped = channel === 'all' ? rows : rows.filter((r) => r.channel_kind === channel);
    const cutoff = Date.now() - 30 * DAY_MS;
    return {
      open: scoped.filter((r) => !RETURN_CLOSED_STATUSES.includes(r.status)).length,
      requested: scoped.filter((r) => r.status === 'requested').length,
      awaitingReceipt: scoped.filter(awaitingReceipt).length,
      awaitingResolution: scoped.filter(awaitingResolution).length,
      refunded30d: scoped
        .filter(
          (r) =>
            r.resolution_status === 'issued' &&
            r.resolution_issued_at &&
            new Date(r.resolution_issued_at).getTime() >= cutoff,
        )
        .reduce((acc, r) => acc + (r.refund_cents ?? 0), 0),
    };
  }, [rows, channel]);

  const insights = useMemo(() => {
    const cutoff = Date.now() - 90 * DAY_MS;
    const recent = rows.filter(
      (r) =>
        new Date(r.requested_at).getTime() >= cutoff &&
        r.status !== 'cancelled' &&
        (channel === 'all' || r.channel_kind === channel),
    );
    const reasons = new Map<ReturnReason, number>();
    const skus = new Map<string, { label: string; units: number }>();
    for (const r of recent) {
      reasons.set(r.reason_code, (reasons.get(r.reason_code) ?? 0) + 1);
      for (const l of r.return_lines) {
        const key = l.items?.sku ?? l.sku ?? returnLineLabel(l);
        const entry = skus.get(key) ?? { label: returnLineLabel(l), units: 0 };
        entry.units += l.quantity;
        skus.set(key, entry);
      }
    }
    return {
      total: recent.length,
      reasons: [...reasons.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6),
      skus: [...skus.entries()].sort((a, b) => b[1].units - a[1].units).slice(0, 6),
    };
  }, [rows, channel]);

  const columns = useMemo<ColumnDef<ReturnRow>[]>(
    () => [
      {
        id: 'rma_number',
        accessorKey: 'rma_number',
        header: 'RMA #',
        size: 110,
        cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs">{row.original.rma_number}</span>,
      },
      {
        id: 'requested_at',
        accessorKey: 'requested_at',
        header: 'Requested',
        size: 110,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.requested_at)}</span>
        ),
      },
      {
        id: 'channel',
        accessorFn: (r) => CHANNEL_RETURN_PROFILES[r.channel_kind].label,
        header: 'Channel',
        size: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <Badge variant="outline">{CHANNEL_RETURN_PROFILES[row.original.channel_kind].label}</Badge>,
      },
      {
        id: 'order',
        accessorFn: (r) => r.sales_orders?.so_number ?? r.external_order_ref ?? '',
        header: 'Order',
        size: 150,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
            {row.original.sales_orders?.so_number ?? row.original.external_order_ref ?? '—'}
          </span>
        ),
      },
      {
        id: 'customer',
        accessorFn: (r) => returnDisplayCustomer(r),
        header: 'Customer',
        size: 180,
      },
      {
        id: 'items',
        accessorFn: (r) => r.return_lines.map((l) => `${l.items?.sku ?? l.sku ?? ''} ${returnLineLabel(l)}`).join(' '),
        header: 'Items',
        size: 220,
        cell: ({ row }) => {
          const lines = row.original.return_lines;
          const units = lines.reduce((acc, l) => acc + l.quantity, 0);
          const head = lines[0];
          if (!head) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="flex flex-col">
              <span className="max-w-[16rem] truncate">
                {head.quantity}× {returnLineLabel(head)}
              </span>
              {lines.length > 1 && (
                <span className="text-[11px] text-muted-foreground">
                  + {lines.length - 1} more · {units} units
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'reason',
        accessorFn: (r) => RETURN_REASON_LABELS[r.reason_code],
        header: 'Reason',
        size: 150,
        filterFn: arrIncludesSome,
      },
      {
        id: 'status',
        accessorFn: (r) => RETURN_STATUS_LABELS[r.status],
        header: 'Status',
        size: 140,
        cell: ({ row }) => (
          <Badge variant={RETURN_STATUS_VARIANTS[row.original.status]}>{RETURN_STATUS_LABELS[row.original.status]}</Badge>
        ),
      },
      {
        id: 'resolution',
        accessorFn: (r) => `${RESOLUTION_LABELS[r.resolution]} ${RESOLUTION_STATUS_LABELS[r.resolution_status]}`,
        header: 'Resolution',
        size: 160,
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs">{RESOLUTION_LABELS[row.original.resolution]}</span>
            <Badge variant={RESOLUTION_STATUS_VARIANTS[row.original.resolution_status]} className="w-fit">
              {RESOLUTION_STATUS_LABELS[row.original.resolution_status]}
            </Badge>
          </div>
        ),
      },
      {
        id: 'value',
        accessorFn: (r) => r.refund_cents ?? returnLinesValueCents(r),
        header: () => <span className="block text-right">Value</span>,
        size: 110,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="block text-right tabular-nums">
              {formatCurrency(r.refund_cents ?? returnLinesValueCents(r))}
              {r.refund_cents == null && <span className="block text-[11px] text-muted-foreground">at price paid</span>}
            </span>
          );
        },
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(() => {
    const reasons = Array.from(new Set(filtered.map((r) => RETURN_REASON_LABELS[r.reason_code]))).sort();
    return [{ columnId: 'reason', title: 'Reason', options: reasons.map((v) => ({ value: v, label: v })) }];
  }, [filtered]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Returns / RMA"
        actions={
          canManage ? (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New RMA
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Open RMAs"
          value={metrics.open.toLocaleString()}
          icon={<Undo2 className="h-4 w-4 text-muted-foreground" />}
          hint={`${metrics.requested} awaiting approval`}
          loading={isLoading}
        />
        <MetricCard
          label="Awaiting receipt"
          value={metrics.awaitingReceipt.toLocaleString()}
          icon={<PackageOpen className="h-4 w-4 text-muted-foreground" />}
          hint="Shipping back to us"
          loading={isLoading}
        />
        <MetricCard
          label="Awaiting refund / credit"
          value={metrics.awaitingResolution.toLocaleString()}
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          loading={isLoading}
        />
        <MetricCard
          label="Refunded (30 days)"
          value={formatCurrency(metrics.refunded30d, { whole: true })}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          loading={isLoading}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={channel} onValueChange={(v) => setChannel(v as ChannelFilter)}>
          <TabsList>
            {(['all', ...RETURN_CHANNEL_ORDER] as ChannelFilter[]).map((c) => (
              <TabsTrigger key={c} value={c} className="gap-1.5">
                {c === 'all' ? 'All channels' : CHANNEL_RETURN_PROFILES[c].label}
                <span className="rounded-full bg-secondary/80 px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                  {channelCounts[c]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {(['all', ...RETURN_CHANNEL_ORDER] as ChannelFilter[]).map((c) => (
            <TabsContent key={c} value={c} className="hidden" />
          ))}
        </Tabs>

        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="h-9 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open RMAs</SelectItem>
            <SelectItem value="all">Any status</SelectItem>
            {RETURN_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {RETURN_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<ReturnRow, unknown>
            data={filtered}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            searchPlaceholder="Search by RMA #, order, customer, SKU…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100]}
            tableId="sales-returns"
            enableColumnResizing
            mobileCard={{
              title: (r) => r.rma_number,
              subtitle: (r) => (
                <span>
                  {formatDate(r.requested_at)} · {returnDisplayCustomer(r)}
                </span>
              ),
              badges: (r) => [
                <Badge key="ch" variant="outline">
                  {CHANNEL_RETURN_PROFILES[r.channel_kind].label}
                </Badge>,
                <Badge key="st" variant={RETURN_STATUS_VARIANTS[r.status]}>
                  {RETURN_STATUS_LABELS[r.status]}
                </Badge>,
              ],
              amount: (r) => ({
                value: formatCurrency(r.refund_cents ?? returnLinesValueCents(r)),
                tone: 'neutral',
                caption: `${RESOLUTION_LABELS[r.resolution]} · ${RESOLUTION_STATUS_LABELS[r.resolution_status]}`,
              }),
              meta: (r) => [
                { label: 'Reason', value: RETURN_REASON_LABELS[r.reason_code] },
                {
                  label: 'Items',
                  value: r.return_lines[0]
                    ? `${r.return_lines[0].quantity}× ${returnLineLabel(r.return_lines[0])}${r.return_lines.length > 1 ? ` +${r.return_lines.length - 1}` : ''}`
                    : '—',
                },
              ],
            }}
            emptyState={
              <EmptyState
                icon={Undo2}
                title={rows.length === 0 ? 'No returns yet' : 'No RMAs match'}
                action={
                  canManage && rows.length === 0 ? (
                    <Button onClick={openCreate}>
                      <Plus className="h-4 w-4" />
                      New RMA
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {insights.total > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <InsightList
            title="Top return reasons"
            rows={insights.reasons.map(([reason, count]) => ({
              key: reason,
              label: RETURN_REASON_LABELS[reason],
              value: count,
              suffix: count === 1 ? 'RMA' : 'RMAs',
            }))}
          />
          <InsightList
            title="Most returned items"
            rows={insights.skus.map(([sku, entry]) => ({
              key: sku,
              label: entry.label,
              sublabel: sku !== entry.label ? sku : undefined,
              value: entry.units,
              suffix: entry.units === 1 ? 'unit' : 'units',
            }))}
          />
        </div>
      )}

      <ReturnDetailSheet
        rma={selected}
        open={selected !== null}
        onOpenChange={(open) => !open && setSelectedId(null)}
        canManage={canManage}
        onEdit={(r) => {
          setEditing(r);
          setDialogOpen(true);
        }}
        onReceive={(r) => setReceiving(r)}
      />

      {canManage && (
        <>
          <ReturnDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            existing={editing}
            onSaved={(id) => setSelectedId(id)}
          />
          <ReceiveReturnDialog
            open={receiving !== null}
            onOpenChange={(open) => !open && setReceiving(null)}
            rma={receiving ? rows.find((r) => r.id === receiving.id) ?? receiving : null}
          />
        </>
      )}
    </div>
  );
}

interface InsightRow {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  suffix: string;
}

function InsightList({ title, rows }: { title: string; rows: InsightRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="truncate">
                {r.label}
                {r.sublabel && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{r.sublabel}</span>}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {r.value} {r.suffix}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary">
              <div className="h-1.5 rounded-full bg-atmosphere-dark" style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
